import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { OFFER_STORES } from "@/components/admin/StoreOffersTable";
import { parseZips } from "@/components/admin/launch/CreateWaveDialog";
import { api } from "@/lib/api";

export const AUDIENCES = { all: "Everyone", free: "Free members", tier: "One tier", founding: "Founding Crush holders", zips: "ZIP list" };
export const PROMO_TIERS = { free: "Free", crushr: "Crushr", winkr: "Winkr", duo: "Duo" };
export const offerLine = (o) => `${OFFER_STORES[o.store] || o.store} · ${o.display_price} / ${o.period}${o.label ? ` · ${o.label}` : ""}`;

// ISO <-> <input type="datetime-local"> (local time)
const toLocal = (iso) => { if (!iso) return ""; const d = new Date(iso); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };
const toIso = (v) => (v ? new Date(v).toISOString() : "");

const initial = (p) => ({ name: p?.name || "", starts_at: toLocal(p?.starts_at), ends_at: toLocal(p?.ends_at), type: p?.audience?.type || "all",
  tier: p?.audience?.tier || "free", zips: (p?.audience?.zips || []).join(", "), offer_ids: p?.offer_ids || [] });

function OfferPicker({ offers, value, onChange }) {
  if (!offers.length) return <p className="cw-error mt-2 text-sm" data-testid="promo-no-offers">No store offers yet. Add them in Pricing › Store offers first.</p>;
  return (
    <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto rounded-xl border border-border p-2" data-testid="promo-offer-list">
      {offers.map((o) => (
        <li key={o.id}><label htmlFor={`po-${o.id}`} className={`flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 text-sm hover:bg-[var(--cw-surface)] ${o.active ? "" : "opacity-60"}`}>
          <Checkbox id={`po-${o.id}`} checked={value.includes(o.id)} onCheckedChange={(v) => onChange(v ? [...value, o.id] : value.filter((x) => x !== o.id))} data-testid={`promo-offer-${o.id}`} />
          <span className="min-w-0 flex-1"><span className="font-medium">{offerLine(o)}</span><span className="cw-muted block truncate font-mono text-xs">{o.product_id} · {o.offer_id}{o.active ? "" : " · inactive"}</span></span>
        </label></li>
      ))}
    </ul>
  );
}

export default function PromotionDialog({ open, onOpenChange, promo, offers, onSaved }) {
  const [f, setF] = useState(() => initial(promo));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const zips = parseZips(f.zips);
  const valid = f.name.trim() && f.starts_at && f.ends_at && new Date(f.ends_at) > new Date(f.starts_at) && f.offer_ids.length && (f.type !== "zips" || zips.length);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    const audience = { type: f.type, ...(f.type === "tier" ? { tier: f.tier } : {}), ...(f.type === "zips" ? { zips } : {}) };
    const body = { name: f.name.trim(), starts_at: toIso(f.starts_at), ends_at: toIso(f.ends_at), audience, offer_ids: f.offer_ids };
    try {
      const res = promo ? await api(`/admin/promotions/${promo.id}`, { method: "PATCH", body }) : await api("/admin/promotions", { body });
      onSaved(res.promotion, !!promo);
      onOpenChange(false);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-[20px] sm:max-w-xl" data-testid="promo-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader><DialogTitle>{promo ? "Edit promotion" : "New promotion"}</DialogTitle>
            <DialogDescription>New promotions start as drafts. Prices come only from store offers, and the app shows a plain end date, never a countdown.</DialogDescription></DialogHeader>
          <div><Label htmlFor="pr-name">Name</Label><Input id="pr-name" value={f.name} onChange={set("name")} className="mt-2" data-testid="promo-name-input" /></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label htmlFor="pr-start">Starts</Label><Input id="pr-start" type="datetime-local" value={f.starts_at} onChange={set("starts_at")} className="mt-2" data-testid="promo-start-input" /></div>
            <div><Label htmlFor="pr-end">Ends</Label><Input id="pr-end" type="datetime-local" value={f.ends_at} onChange={set("ends_at")} className="mt-2" data-testid="promo-end-input" /></div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label htmlFor="pr-aud">Audience</Label>
              <Select value={f.type} onValueChange={set("type")}><SelectTrigger id="pr-aud" className="mt-2" data-testid="promo-audience-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(AUDIENCES).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`promo-audience-option-${k}`}>{l}</SelectItem>)}</SelectContent></Select></div>
            {f.type === "tier" && <div><Label htmlFor="pr-tier">Tier</Label>
              <Select value={f.tier} onValueChange={set("tier")}><SelectTrigger id="pr-tier" className="mt-2" data-testid="promo-tier-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PROMO_TIERS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></div>}
          </div>
          {f.type === "zips" && <div><Label htmlFor="pr-zips">ZIPs (home ZIP)</Label><Textarea id="pr-zips" rows={2} value={f.zips} onChange={set("zips")} className="mt-2 font-mono text-sm" data-testid="promo-zips-input" /></div>}
          <div><Label>Store offers</Label><OfferPicker offers={offers} value={f.offer_ids} onChange={set("offer_ids")} /></div>
          {error && <p className="cw-error text-sm" role="alert" data-testid="promo-error">{error}</p>}
          <DialogFooter><Button type="submit" disabled={!valid || busy} data-testid="promo-save-button">{busy ? "Saving…" : promo ? "Save changes" : "Create draft"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
